import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
  Typography,
  Alert,
  Chip,
  Box,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TitleOutlinedIcon from "@mui/icons-material/TitleOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import AgendaMeetingLinks from "./AgendaMeetingLinks";
import AgendaEventDescription from "./AgendaEventDescription";
import AgendaFindTimeDialog from "./AgendaFindTimeDialog";
import AgendaVideoCallSummary from "./AgendaVideoCallSummary";
import { extractAgendaMeetingLinks } from "../../utils/agendaMeetingLinks";
import { getAgendaEventColors, isAgendaEventPast, readAgendaEventStatus } from "./agendaUtils";
import { alpha, useTheme } from "@mui/material/styles";
import { useUsers } from "../../hooks/useUsers";
import { AuthContext } from "../../context/AuthContext";
import { fetchAgendaConflicts, getGoogleCalendarStatus } from "../../api/client";

const emptyForm = {
  title: "",
  start: "",
  end: "",
  allDay: false,
  location: "",
  description: "",
  attendees: [],
  calendarId: "",
  videoMeeting: "xpro",
  requireCamera: false,
};

function toDatetimeLocal(value) {
  if (!value) return "";
  const d = value instanceof Date ? dayjs(value) : dayjs(String(value).replace(" ", "T"));
  if (!d.isValid()) return "";
  return d.format("YYYY-MM-DDTHH:mm");
}

function fromDatetimeLocal(value, allDay) {
  if (!value) return null;
  if (allDay) return dayjs(value).format("YYYY-MM-DD 00:00:00");
  return dayjs(value).format("YYYY-MM-DD HH:mm:ss");
}

function formatReadOnlyWhen(start, end, allDay) {
  const s = dayjs(String(start).replace(" ", "T"));
  const e = end ? dayjs(String(end).replace(" ", "T")) : null;
  if (!s.isValid()) return "";

  if (allDay) {
    if (e && e.isValid() && !e.isSame(s, "day")) {
      return `${s.format("DD/MM/YYYY")} – ${e.format("DD/MM/YYYY")}`;
    }
    return s.format("DD/MM/YYYY");
  }

  const startLabel = s.format("DD/MM/YYYY [às] HH:mm");
  if (e && e.isValid() && !e.isSame(s)) {
    const sameDay = e.isSame(s, "day");
    const endLabel = sameDay ? e.format("HH:mm") : e.format("DD/MM/YYYY [às] HH:mm");
    return `${startLabel} – ${endLabel}`;
  }
  return startLabel;
}

function extractEmail(value = "") {
  const s = String(value || "").trim();
  const m = s.match(/<?([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})>?/i);
  return m ? m[1].toLowerCase() : "";
}

function normalizeAttendee(value) {
  const email =
    typeof value === "string"
      ? extractEmail(value)
      : extractEmail(value?.email || value?.value || value?.label || "");
  if (!email) return null;
  const name =
    typeof value === "string"
      ? String(value).includes("<")
        ? String(value).split("<")[0].trim().replace(/^["']|["']$/g, "")
        : ""
      : String(value?.name || value?.nome || "").trim();
  return { email, ...(name && name.toLowerCase() !== email ? { name } : {}) };
}

function normalizeAttendees(values) {
  const byEmail = new Map();
  (values || []).forEach((value) => {
    const attendee = normalizeAttendee(value);
    if (attendee?.email) byEmail.set(attendee.email, attendee);
  });
  return [...byEmail.values()];
}

function attendeeLabel(attendee) {
  if (!attendee) return "";
  if (typeof attendee === "string") return attendee;
  return attendee.name ? `${attendee.name} <${attendee.email}>` : attendee.email || "";
}

function getStatusDescriptionKey(status, { hasLocation, hasMeetingLinks, isPast, isGoogle }) {
  if (status === "canceled") {
    return isGoogle ? "statusDescriptions.canceledGoogle" : "statusDescriptions.canceled";
  }
  if (status === "completed") {
    if (isPast && isGoogle) return "statusDescriptions.completedPastGoogle";
    if (isPast) return "statusDescriptions.completedPast";
    return "statusDescriptions.completed";
  }
  if (hasLocation && hasMeetingLinks) return "statusDescriptions.pending.locationAndLinks";
  if (hasLocation) return "statusDescriptions.pending.location";
  if (hasMeetingLinks) return "statusDescriptions.pending.links";
  return "statusDescriptions.pending.default";
}

function formatConflictWhen(start, end) {
  const s = dayjs(String(start || "").replace(" ", "T"));
  const e = dayjs(String(end || "").replace(" ", "T"));
  if (!s.isValid() || !e.isValid() || !e.isAfter(s)) return null;

  const spansFullDay =
    s.hour() === 0 &&
    s.minute() === 0 &&
    e.diff(s, "hour") >= 20 &&
    (e.hour() === 0 || e.hour() === 23);

  if (spansFullDay) {
    return e.isSame(s, "day") || (e.hour() === 0 && e.diff(s, "day") <= 1)
      ? "Dia todo"
      : `${s.format("DD/MM")} · dia todo`;
  }

  if (e.isSame(s, "day")) {
    return `${s.format("HH:mm")}–${e.format("HH:mm")}`;
  }
  return `${s.format("DD/MM HH:mm")}–${e.format("DD/MM HH:mm")}`;
}

export default function AgendaEventDialog({
  open,
  onClose,
  initialEvent,
  defaultRange,
  onSave,
  onDelete,
  onStatusChange,
  saving,
  error,
  calendars = [],
  defaultCalendarId,
}) {
  const { t } = useTranslation("teamAgenda");
  const theme = useTheme();
  const { userData } = useContext(AuthContext);
  const [form, setForm] = useState(emptyForm);
  const [findTimeOpen, setFindTimeOpen] = useState(false);
  const { data: usersData } = useUsers(1, 200, "", "humanos");
  const currentUserEmail = String(userData?.email || "")
    .trim()
    .toLowerCase();
  const currentUserName = String(userData?.nome || userData?.name || "").trim();
  const isEdit = Boolean(initialEvent?.editable && initialEvent?.meta?.eventId);
  const readOnly = Boolean(initialEvent && !initialEvent.editable);
  const { data: googleStatus } = useQuery(
    ["google-calendar-status-agenda-event"],
    async () => {
      const res = await getGoogleCalendarStatus();
      return res && typeof res === "object" && "data" in res ? res.data : res;
    },
    { staleTime: 60_000, enabled: open && !readOnly && !isEdit }
  );
  const googleConnected = Boolean(googleStatus?.connected);
  const source = initialEvent?.source;
  const sourceColor = source ? getAgendaEventColors(theme)[source] : undefined;
  const currentStatus = readAgendaEventStatus(initialEvent);
  const canChangeStatus = Boolean(initialEvent?.meta?.eventId && initialEvent?.statusEditable);
  const isCompleted = currentStatus === "completed";
  const isCanceled = currentStatus === "canceled";
  const isClosedStatus = isCompleted || isCanceled;
  const isGoogle = source === "google";
  const isPastEvent = Boolean(initialEvent && isAgendaEventPast(initialEvent));
  const cannotReopen = isPastEvent;
  const reopenHint = cannotReopen
    ? t(isGoogle ? "statusReopenNeedsFutureDateGoogle" : "statusReopenNeedsFutureDate")
    : "";

  const conflictPayload = useMemo(() => {
    if (readOnly || !form.start || !form.attendees?.length) return null;
    const start = fromDatetimeLocal(form.start, form.allDay);
    const end = fromDatetimeLocal(form.end || form.start, form.allDay) || start;
    if (!start) return null;
    return {
      start,
      end,
      attendees: form.attendees,
      excludeEventId: initialEvent?.meta?.eventId || undefined,
    };
  }, [readOnly, form.start, form.end, form.allDay, form.attendees, initialEvent?.meta?.eventId]);

  const { data: conflictsData, isFetching: conflictsLoading } = useQuery(
    ["agenda-conflicts", conflictPayload],
    async () => {
      const res = await fetchAgendaConflicts(conflictPayload);
      return res && typeof res === "object" && "data" in res ? res.data : res;
    },
    {
      enabled: Boolean(open && conflictPayload),
      staleTime: 15_000,
      keepPreviousData: true,
    }
  );

  const durationMinutes = useMemo(() => {
    if (!form.start || !form.end || form.allDay) return 60;
    const start = dayjs(form.start);
    const end = dayjs(form.end);
    if (!start.isValid() || !end.isValid()) return 60;
    const mins = end.diff(start, "minute");
    return mins > 0 ? mins : 60;
  }, [form.start, form.end, form.allDay]);

  const findTimeAttendees = useMemo(() => {
    const guests = normalizeAttendees(form.attendees || []);
    if (!currentUserEmail) return guests;
    const self = {
      email: currentUserEmail,
      ...(currentUserName && currentUserName.toLowerCase() !== currentUserEmail
        ? { name: currentUserName }
        : {}),
    };
    const withoutSelf = guests.filter((a) => a.email !== currentUserEmail);
    return [self, ...withoutSelf];
  }, [form.attendees, currentUserEmail, currentUserName]);

  const meetingLinks = useMemo(
    () =>
      extractAgendaMeetingLinks({
        location: readOnly ? initialEvent?.meta?.location : form.location,
        description: readOnly ? initialEvent?.meta?.description : form.description,
        meetingLinks: initialEvent?.meta?.meetingLinks,
      }),
    [readOnly, initialEvent, form.location, form.description]
  );

  const hasDescription = Boolean(initialEvent?.meta?.description?.trim());
  const userOptions = useMemo(
    () =>
      (usersData?.users || [])
        .filter((user) => user?.email)
        .map((user) => ({
          email: String(user.email).trim().toLowerCase(),
          name: user.nome || user.name || "",
        })),
    [usersData]
  );
  const currentAttendees = normalizeAttendees(initialEvent?.meta?.attendees || initialEvent?.attendees || []);
  const activeVideo = initialEvent?.activeVideo || null;
  const showMeetingLinks =
    meetingLinks.length > 0 ||
    Boolean(activeVideo?.canJoin) ||
    Boolean(activeVideo?.isPrivate && !activeVideo?.canJoin);
  const videoCallSummary = initialEvent?.meta?.videoCallSummary || null;
  const eventLocation = readOnly ? initialEvent?.meta?.location : form.location;
  const statusDescriptionKey = getStatusDescriptionKey(currentStatus || "pending", {
    hasLocation: Boolean(String(eventLocation || "").trim()),
    hasMeetingLinks: meetingLinks.length > 0,
    isPast: isPastEvent,
    isGoogle,
  });
  const selectableCalendars = useMemo(
    () => calendars.filter((calendar) => calendar?.canCreate || calendar?.canManage),
    [calendars]
  );
  const currentCalendar = useMemo(
    () =>
      calendars.find(
        (calendar) =>
          Number(calendar.id) === Number(form.calendarId || initialEvent?.calendarId || initialEvent?.meta?.calendarId)
      ) || null,
    [calendars, form.calendarId, initialEvent]
  );

  useEffect(() => {
    if (!open) return;
    if (initialEvent) {
      setForm({
        title: initialEvent.title || "",
        start: toDatetimeLocal(initialEvent.start),
        end: toDatetimeLocal(initialEvent.end || initialEvent.start),
        allDay: Boolean(initialEvent.allDay),
        location: initialEvent.meta?.location || "",
        description: initialEvent.meta?.description || "",
        attendees: normalizeAttendees(initialEvent.meta?.attendees || initialEvent.attendees || []),
        calendarId: initialEvent.calendarId || initialEvent.meta?.calendarId || defaultCalendarId || "",
        videoMeeting: "none",
        requireCamera: false,
      });
    } else if (defaultRange) {
      setForm({
        ...emptyForm,
        title: defaultRange.title || "",
        start: toDatetimeLocal(defaultRange.start),
        end: toDatetimeLocal(defaultRange.end || defaultRange.start),
        allDay: Boolean(defaultRange.allDay),
        attendees: normalizeAttendees(defaultRange.attendees || []),
        calendarId: defaultRange.calendarId || defaultCalendarId || selectableCalendars[0]?.id || "",
      });
    } else {
      setForm({ ...emptyForm, calendarId: defaultCalendarId || selectableCalendars[0]?.id || "" });
    }
  }, [open, initialEvent, defaultRange, defaultCalendarId, selectableCalendars]);

  const handleSubmit = async () => {
    const payload = {
      title: form.title.trim(),
      start: fromDatetimeLocal(form.start, form.allDay),
      end: fromDatetimeLocal(form.end, form.allDay),
      allDay: form.allDay,
      location: form.location.trim() || null,
      description: form.description.trim() || null,
      attendees: normalizeAttendees(form.attendees),
      calendarId: form.calendarId || undefined,
    };
    if (!isEdit && form.videoMeeting && form.videoMeeting !== "none") {
      payload.videoMeeting = form.videoMeeting;
      if (form.videoMeeting === "xpro" && form.requireCamera) {
        payload.requireCamera = true;
      }
    }
    await onSave?.(payload, initialEvent?.meta?.eventId);
  };

  const handleStatusChange = async (status) => {
    if (!initialEvent?.meta?.eventId) return;
    await onStatusChange?.(initialEvent.meta.eventId, status);
  };

  const handleStatusToggle = (_, value) => {
    if (!value) return;
    const nextStatus = value === "pending" ? null : value;
    if ((currentStatus || null) === nextStatus) return;
    if (nextStatus == null && cannotReopen) return;
    handleStatusChange(nextStatus);
  };

  const dialogTitle = readOnly ? t("viewEvent") : isEdit ? t("editEvent") : t("newEvent");
  const dialogSubtitle = !readOnly ? (isEdit ? t("editEventSubtitle") : t("newEventSubtitle")) : "";
  const statusTone = isCanceled
    ? {
        color: theme.palette.error.main,
        bg: alpha(theme.palette.error.main, theme.palette.mode === "dark" ? 0.18 : 0.08),
        border: alpha(theme.palette.error.main, 0.28),
        icon: <CancelOutlinedIcon />,
      }
    : isCompleted
      ? {
          color: theme.palette.success.main,
          bg: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.18 : 0.08),
          border: alpha(theme.palette.success.main, 0.28),
          icon: <CheckCircleOutlineIcon />,
        }
      : {
          color: theme.palette.info.main,
          bg: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.16 : 0.07),
          border: alpha(theme.palette.info.main, 0.24),
          icon: <InfoOutlinedIcon />,
        };
  const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 2 },
    "& .MuiInputBase-input::placeholder": { color: "text.disabled", opacity: 1 },
  };
  const formSectionSx = {
    p: 1.5,
    border: 1,
    borderColor: "divider",
    borderRadius: 2.5,
    bgcolor: alpha(theme.palette.background.default, theme.palette.mode === "dark" ? 0.24 : 0.5),
  };
  const subtleAdornmentSx = { color: "text.secondary", mr: 0.5 };
  const statusActions = (
    <Box
      sx={{
        border: 1,
        borderColor: isClosedStatus ? statusTone.border : "divider",
        borderRadius: 2.5,
        px: 1.25,
        py: 1,
        bgcolor: isClosedStatus
          ? statusTone.bg
          : alpha(theme.palette.background.default, theme.palette.mode === "dark" ? 0.24 : 0.55),
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              color: statusTone.color,
              bgcolor: alpha(statusTone.color, theme.palette.mode === "dark" ? 0.16 : 0.1),
              flexShrink: 0,
              "& svg": { fontSize: 20 },
            }}
          >
            {statusTone.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={800}>
              {t(`statusTitles.${currentStatus || "pending"}`)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(statusDescriptionKey)}
            </Typography>
          </Box>
        </Stack>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={currentStatus || "pending"}
          onChange={handleStatusToggle}
          disabled={saving}
          sx={{
            flexShrink: 0,
            alignSelf: { xs: "stretch", sm: "center" },
            "& .MuiToggleButton-root": {
              px: 1.15,
              py: 0.65,
              gap: 0.5,
              textTransform: "none",
              fontWeight: 700,
              borderColor: "divider",
            },
            "& .Mui-selected[value='pending']": {
              color: theme.palette.text.primary,
              bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.18 : 0.08),
            },
            "& .Mui-selected[value='completed']": {
              color: theme.palette.success.main,
              bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.22 : 0.1),
            },
            "& .Mui-selected[value='canceled']": {
              color: theme.palette.error.main,
              bgcolor: alpha(theme.palette.error.main, theme.palette.mode === "dark" ? 0.22 : 0.1),
            },
          }}
        >
          <ToggleButton
            value="pending"
            aria-label={t("statuses.pending")}
            disabled={saving || cannotReopen}
            title={reopenHint || undefined}
          >
            <RadioButtonUncheckedIcon fontSize="small" />
            {t("statuses.pending")}
          </ToggleButton>
          <ToggleButton value="completed" aria-label={t("statuses.completed")}>
            <CheckCircleOutlineIcon fontSize="small" />
            {t("statuses.completed")}
          </ToggleButton>
          <ToggleButton value="canceled" aria-label={t("statuses.canceled")}>
            <CancelOutlinedIcon fontSize="small" />
            {t("statuses.canceled")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Box>
  );

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle sx={{ px: 3, pt: 3, pb: readOnly ? 1 : 1.5 }}>
        <Stack spacing={0.75}>
          <Typography variant="h6" component="span" fontWeight={700}>
            {dialogTitle}
          </Typography>
          {!readOnly ? (
            <Typography variant="body2" color="text.secondary">
              {dialogSubtitle}
            </Typography>
          ) : null}
          {readOnly && source ? (
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              {readOnly && source ? (
                <>
                  <Chip
                    size="small"
                    label={t(`sources.${source}`)}
                    sx={{
                      height: 24,
                      fontWeight: 600,
                      bgcolor: sourceColor ? `${sourceColor}22` : undefined,
                      color: sourceColor || "text.secondary",
                      border: sourceColor ? `1px solid ${sourceColor}55` : undefined,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t(`sourceHints.${source}`)}
                  </Typography>
                </>
              ) : null}
            </Stack>
          ) : null}
          {readOnly && (currentCalendar || initialEvent?.calendarName || initialEvent?.meta?.calendarName) ? (
            <Chip
              size="small"
              label={currentCalendar?.name || initialEvent?.calendarName || initialEvent?.meta?.calendarName}
              sx={{
                height: 24,
                alignSelf: "flex-start",
                fontWeight: 600,
                bgcolor: alpha(currentCalendar?.color || initialEvent?.calendarColor || "#1976d2", 0.12),
                color: currentCalendar?.color || initialEvent?.calendarColor || "text.secondary",
                border: `1px solid ${alpha(currentCalendar?.color || initialEvent?.calendarColor || "#1976d2", 0.35)}`,
              }}
            />
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto", px: 3 }}>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {readOnly ? (
            <>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1.3}>
                  {form.title || t("untitledEvent")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {formatReadOnlyWhen(initialEvent?.start, initialEvent?.end, form.allDay)}
                  {form.allDay ? ` · ${t("allDay")}` : null}
                </Typography>
              </Box>

              {canChangeStatus && isClosedStatus ? statusActions : null}

              {showMeetingLinks ? (
                <AgendaMeetingLinks
                  location={initialEvent?.meta?.location}
                  description={initialEvent?.meta?.description}
                  meetingLinks={initialEvent?.meta?.meetingLinks}
                  activeVideo={initialEvent?.activeVideo || null}
                />
              ) : null}

              {videoCallSummary ? <AgendaVideoCallSummary summary={videoCallSummary} /> : null}

              {canChangeStatus && !isClosedStatus ? statusActions : null}

              {currentAttendees.length ? (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    display="block"
                    sx={{ mb: 0.75 }}
                  >
                    {t("fieldGuests")}
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {currentAttendees.map((attendee) => (
                      <Chip key={attendee.email} size="small" label={attendeeLabel(attendee)} />
                    ))}
                  </Stack>
                </Box>
              ) : null}

              {initialEvent?.meta?.location ? (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    display="block"
                    sx={{ mb: 0.5 }}
                  >
                    {t("fieldLocation")}
                  </Typography>
                  <Typography variant="body2">{initialEvent.meta.location}</Typography>
                </Box>
              ) : null}

              {hasDescription ? (
                <AgendaEventDescription
                  description={initialEvent?.meta?.description}
                  location={initialEvent?.meta?.location}
                  meetingLinks={initialEvent?.meta?.meetingLinks}
                />
              ) : null}

            </>
          ) : (
            <>
              {meetingLinks.length || activeVideo ? (
                <AgendaMeetingLinks
                  location={form.location}
                  description={form.description}
                  meetingLinks={initialEvent?.meta?.meetingLinks}
                  activeVideo={activeVideo}
                />
              ) : null}
              {videoCallSummary ? <AgendaVideoCallSummary summary={videoCallSummary} /> : null}
              {selectableCalendars.length > 1 || form.calendarId ? (
                <TextField
                  select
                  label={t("fieldCalendar", "Agenda")}
                  value={form.calendarId || ""}
                  onChange={(e) => setForm((f) => ({ ...f, calendarId: e.target.value }))}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  helperText={t("fieldCalendarHint", "Escolha onde este compromisso será exibido e compartilhado.")}
                >
                  {selectableCalendars.map((calendar) => (
                    <MenuItem key={calendar.id} value={calendar.id}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: calendar.color || "primary.main",
                          }}
                        />
                        <span>{calendar.name}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              <TextField
                label={t("fieldTitle")}
                placeholder={t("fieldTitlePlaceholder")}
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                fullWidth
                required
                size="small"
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TitleOutlinedIcon fontSize="small" sx={subtleAdornmentSx} />
                    </InputAdornment>
                  ),
                }}
              />
              <Autocomplete
                multiple
                freeSolo
                filterSelectedOptions
                size="small"
                options={userOptions}
                value={form.attendees}
                onChange={(_, value) => setForm((f) => ({ ...f, attendees: normalizeAttendees(value) }))}
                getOptionLabel={attendeeLabel}
                isOptionEqualToValue={(option, value) =>
                  normalizeAttendee(option)?.email === normalizeAttendee(value)?.email
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const tagProps = getTagProps({ index });
                    return (
                      <Chip
                        key={normalizeAttendee(option)?.email || index}
                        {...tagProps}
                        label={attendeeLabel(option)}
                        size="small"
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("fieldGuests")}
                    placeholder={t("fieldGuestsPlaceholder")}
                    helperText={t("fieldGuestsHint")}
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <GroupOutlinedIcon fontSize="small" sx={subtleAdornmentSx} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {form.attendees?.length ? (
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      {conflictsLoading
                        ? "Verificando agendas…"
                        : conflictsData?.hasConflict
                          ? "Há conflito de horário"
                          : "Sem conflito no horário escolhido"}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<EventAvailableOutlinedIcon fontSize="small" />}
                      onClick={() => setFindTimeOpen(true)}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      Encontrar horário
                    </Button>
                  </Stack>
                  {conflictsData?.hasConflict ? (
                    <Alert severity="warning" icon={<WarningAmberOutlinedIcon fontSize="inherit" />}>
                      <Stack spacing={0.75}>
                        {(conflictsData.attendees || [])
                          .filter((a) => !a.free)
                          .map((a) => (
                            <Box key={a.email}>
                              <Typography variant="body2" fontWeight={700}>
                                {a.name || a.email}
                              </Typography>
                              {(a.conflicts || []).slice(0, 3).map((c, idx) => {
                                const when = formatConflictWhen(c.start, c.end);
                                if (!when) return null;
                                return (
                                  <Typography key={`${a.email}-${idx}`} variant="caption" color="text.secondary" display="block">
                                    {a.detailLevel === "full" && c.title
                                      ? `${c.title} · ${when}`
                                      : `Ocupado · ${when}`}
                                  </Typography>
                                );
                              })}
                              {a.detailLevel === "busy_only" ? (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Agenda não compartilhada — detalhes do compromisso ocultos.
                                </Typography>
                              ) : null}
                            </Box>
                          ))}
                      </Stack>
                    </Alert>
                  ) : null}
                </Stack>
              ) : null}
              <Box sx={formSectionSx}>
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AccessTimeOutlinedIcon fontSize="small" color="action" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {t("sectionWhen")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("sectionWhenHint")}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={form.allDay}
                          onChange={(e) => setForm((f) => ({ ...f, allDay: e.target.checked }))}
                        />
                      }
                      label={t("fieldAllDay")}
                      sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: "0.875rem", fontWeight: 600 } }}
                    />
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                    <TextField
                      label={t("fieldStart")}
                      type={form.allDay ? "date" : "datetime-local"}
                      value={form.allDay ? form.start.slice(0, 10) : form.start}
                      onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                      fullWidth
                      size="small"
                      sx={fieldSx}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label={t("fieldEnd")}
                      type={form.allDay ? "date" : "datetime-local"}
                      value={form.allDay ? (form.end || form.start).slice(0, 10) : form.end}
                      onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                      fullWidth
                      size="small"
                      sx={fieldSx}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                </Stack>
              </Box>
              <Box sx={formSectionSx}>
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <PlaceOutlinedIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {t("sectionWhere")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("sectionWhereHint")}
                      </Typography>
                    </Box>
                  </Stack>

                  {!isEdit ? (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      {[
                        {
                          value: "none",
                          title: t("videoMeeting.none"),
                          description: t("videoMeeting.noneHint"),
                        },
                        {
                          value: "xpro",
                          title: t("videoMeeting.xpro"),
                          description: t("videoMeeting.xproHint"),
                        },
                        {
                          value: "google_meet",
                          title: t("videoMeeting.googleMeet"),
                          description: googleConnected
                            ? t("videoMeeting.googleMeetHint")
                            : t("videoMeeting.googleRequired"),
                          disabled: !googleConnected,
                        },
                      ].map((option) => {
                        const selected = form.videoMeeting === option.value;
                        return (
                          <Box
                            key={option.value}
                            role="button"
                            tabIndex={option.disabled ? -1 : 0}
                            aria-pressed={selected}
                            aria-disabled={option.disabled || undefined}
                            onClick={() => {
                              if (option.disabled) return;
                              setForm((f) => ({ ...f, videoMeeting: option.value }));
                            }}
                            onKeyDown={(event) => {
                              if (option.disabled) return;
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                setForm((f) => ({ ...f, videoMeeting: option.value }));
                              }
                            }}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              px: 1.25,
                              py: 1.1,
                              borderRadius: 2,
                              border: 1,
                              borderColor: selected ? "primary.main" : "divider",
                              bgcolor: selected
                                ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.06)
                                : alpha(theme.palette.background.paper, 0.6),
                              opacity: option.disabled ? 0.55 : 1,
                              cursor: option.disabled ? "not-allowed" : "pointer",
                              transition: "border-color 120ms ease, background-color 120ms ease",
                            }}
                          >
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.35 }}>
                              {option.value === "none" ? (
                                <PlaceOutlinedIcon sx={{ fontSize: 16, color: selected ? "primary.main" : "text.secondary" }} />
                              ) : (
                                <VideocamOutlinedIcon sx={{ fontSize: 16, color: selected ? "primary.main" : "text.secondary" }} />
                              )}
                              <Typography variant="body2" fontWeight={800} noWrap>
                                {option.title}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.35 }}>
                              {option.description}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : null}

                  {form.videoMeeting === "xpro" && !isEdit ? (
                    <FormControlLabel
                      sx={{ alignItems: "flex-start", ml: 0, mr: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(form.requireCamera)}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, requireCamera: e.target.checked }))
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {t("videoMeeting.requireCamera", "Câmera obrigatória")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.35 }}>
                            {t(
                              "videoMeeting.requireCameraHint",
                              "Só entra quem tiver câmera neste dispositivo. O vídeo não poderá ser desligado."
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.35, mt: 0.5 }}>
                            {t(
                              "videoMeeting.requireCameraUseHint",
                              "Use só quando o vídeo for necessário. Não substitui reunião com câmera opcional nem outro jeito de participar."
                            )}
                          </Typography>
                        </Box>
                      }
                    />
                  ) : null}

                  {form.videoMeeting !== "none" && !isEdit ? (
                    <Alert severity="info" icon={<VideocamOutlinedIcon fontSize="inherit" />} sx={{ py: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {form.videoMeeting === "google_meet"
                          ? t("videoMeeting.linkReadyMeet")
                          : t("videoMeeting.linkReadyXpro")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("videoMeeting.placeOptionalHint")}
                      </Typography>
                    </Alert>
                  ) : null}

                  <TextField
                    label={
                      form.videoMeeting !== "none" && !isEdit
                        ? t("fieldLocationWithVideo")
                        : t("fieldLocation")
                    }
                    placeholder={
                      form.videoMeeting !== "none" && !isEdit
                        ? t("fieldLocationWithVideoPlaceholder")
                        : t("fieldLocationPlaceholder")
                    }
                    helperText={
                      form.videoMeeting !== "none" && !isEdit
                        ? t("fieldLocationWithVideoHint")
                        : t("fieldLocationHint")
                    }
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PlaceOutlinedIcon fontSize="small" sx={subtleAdornmentSx} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Box>
              <TextField
                label={t("fieldDescription")}
                placeholder={t("fieldDescriptionPlaceholder")}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                fullWidth
                multiline
                minRows={2}
                helperText={t("fieldDescriptionHint")}
                size="small"
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                      <NotesOutlinedIcon fontSize="small" sx={subtleAdornmentSx} />
                    </InputAdornment>
                  ),
                }}
              />
              {canChangeStatus ? statusActions : null}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: alpha(theme.palette.background.default, theme.palette.mode === "dark" ? 0.2 : 0.55),
        }}
      >
        {isEdit && !readOnly ? (
          <Button color="error" onClick={() => onDelete?.(initialEvent.meta.eventId)} disabled={saving}>
            {t("deleteEvent")}
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 600 }}>
          {readOnly ? t("close") : t("cancel")}
        </Button>
        {!readOnly ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !form.title.trim()}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("save")}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
    <AgendaFindTimeDialog
      open={findTimeOpen}
      onClose={() => setFindTimeOpen(false)}
      attendees={findTimeAttendees}
      organizerEmail={currentUserEmail || null}
      initialStart={form.start}
      durationMinutes={durationMinutes}
      excludeEventId={initialEvent?.meta?.eventId || null}
      onPickSlot={({ start, end, allDay }) => {
        setForm((f) => ({
          ...f,
          start,
          end,
          allDay: Boolean(allDay),
        }));
      }}
    />
    </>
  );
}
